import { useCurrentCommunityId } from '@communities/community/hooks/useCurrentCommunityId';
import type { Topic } from '@communities/content-management/domain';
import { useCommunityTopics } from '@communities/content-management/hooks';
import { HeroBackground } from '@core/components';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { AbrFilterSelector } from '@moderation/components/AbrFilterSelector';
import { CommunityModerationHeaderSection, ModerationStatsSection, DEFAULT_MODERATION_STATS } from '@moderation/components/CommunityModerationHeaderSection';
import { ModerationErrorState } from '@moderation/components/ModerationErrorState';
import { ModerationQueueTable } from '@moderation/components/ModerationQueueTable';
import type { ModerationTopicStats } from '@moderation/domain/ModerationCommunity';
import { useCommunityDetailForModeration } from '@moderation/hooks';
import { Heading } from '@soteria/design-system-ve';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as styles from './CommunityModerationDashboardPage.css';

const VALID_TIME_RANGES = new Set<string>(['24h', '48h', '72h']);

/**
 * Formats seconds into a human-readable duration string
 */
function formatSLADuration(seconds: number): string {
  if (seconds <= 0) return 'Not set';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else if (minutes > 0) {
    return `${minutes}m`;
  }

  return `${seconds}s`;
}

/**
 * Merges configured topics with their moderation stats into table-ready rows.
 */
function buildTopicTableRows(configuredTopics: Topic[], topicStats: ModerationTopicStats[]) {
  const statsMap = new Map(topicStats.map(stat => [stat.topicName, stat]));

  return configuredTopics.map(configTopic => {
    const stat = statsMap.get(configTopic.name);

    return {
      name: configTopic.name,
      caseSLA: formatSLADuration(configTopic.humanModerationCasesSlaInSeconds),
      openCases: stat?.openCasesCount ?? 0,
      nearSLA: stat?.nearSLACases ?? 0,
      pastSLA: stat?.outOfSLACases ?? 0,
    };
  });
}

/**
 * Checks whether the error state should be shown (has errors and not still loading).
 */
function shouldShowErrorState(errors: unknown[] | null, isLoading: boolean): errors is unknown[] {
  return Array.isArray(errors) && errors.length > 0 && !isLoading;
}

/**
 * Calculates aggregate moderation stats from per-topic stats.
 */
function calculateAggregateStats(topicStats: ModerationTopicStats[]) {
  if (!topicStats.length) {
    return {
      totalCases: 0,
      casesOpen: 0,
      activeModerators: 0,
      casesCompleted: 0,
    };
  }

  const totalOpen = topicStats.reduce((sum, topic) => sum + topic.openCasesCount, 0);
  const totalAssigned = topicStats.reduce((sum, topic) => sum + topic.assignedCasesCount, 0);
  const totalInSLA = topicStats.reduce((sum, topic) => sum + topic.inSLACases, 0);

  return {
    totalCases: totalOpen + totalAssigned,
    casesOpen: totalOpen,
    activeModerators: 0, // API integration pending
    casesCompleted: totalInSLA,
  };
}

/**
 * Community Moderation Dashboard Page
 *
 * Main landing page for community moderation showing queue overview and stats
 */

export function CommunityModerationDashboardPage() {
  // Scroll to top when page loads for accessibility and standard web practice
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const navigate = useNavigate();
  const communityId = useCurrentCommunityId();
  const { community, loading: communityLoading, errors, refetch } = useCommunityDetailForModeration({ pollInterval: 30000 });

  // Time range state for stats section
  const [selectedTimeRange, setSelectedTimeRange] = useState<'24h' | '48h' | '72h'>('24h');

  const handleTimeRangeChange = (value: string) => {
    if (VALID_TIME_RANGES.has(value)) {
      setSelectedTimeRange(value as '24h' | '48h' | '72h');
    }
  };

  // Fetch configured topics for the community
  const { topics: configuredTopics, loading: topicsLoading } = useCommunityTopics(communityId);

  // Get moderation stats from community data (fetched as part of community query)
  const topicStats = useMemo(() => community?.moderationStats || [], [community?.moderationStats]);

  // Calculate aggregate stats from topic stats
  const stats = useMemo(() => calculateAggregateStats(topicStats), [topicStats]);

  // Transform API data to table format, merging configured topics with stats
  const topics = useMemo(() => buildTopicTableRows(configuredTopics, topicStats), [configuredTopics, topicStats]);

  // ABR filter state
  const isAbrFilteringEnabled = useIsFeatureEnabled(FEATURE_FLAGS.ABR_FILTERING);
  const [selectedFilterId, setSelectedFilterId] = useState<string | undefined>(undefined);

  const handleStartModerate = (topic: { name: string; caseSLA: string; openCases: number }) => {
    // Navigate to moderation work queue page, optionally with filterId
    const topicId = topic.name.toLowerCase().replace(/\s+/g, '-');
    const filterParam = selectedFilterId ? `?filterId=${encodeURIComponent(selectedFilterId)}` : '';

    navigate(`/communities/${communityId}/moderation/${topicId}/queue${filterParam}`);
  };

  const isLoading = communityLoading || topicsLoading;

  if (shouldShowErrorState(errors, isLoading)) {
    return <ModerationErrorState errors={errors} title="Error Loading Moderation Dashboard" onRetry={() => refetch()} />;
  }

  return (
    <div>
      {/* Hero Background with Community Image */}
      <HeroBackground imageUrl={community?.superHeroArtUrl ?? undefined} />

      {/* Header Section */}
      <CommunityModerationHeaderSection community={community} />

      {/* Main Content Section */}
      <div className={styles.contentSection}>
        <div className={styles.contentContainer}>
          {/* Stats Section */}
          <ModerationStatsSection
            isLoading={isLoading}
            selectedTimeRange={selectedTimeRange}
            stats={stats || DEFAULT_MODERATION_STATS}
            onTimeRangeChange={handleTimeRangeChange}
          />

          {/* Section Header */}
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <Heading level="h2">
                {community?.name} Moderation Queue ({topics.length})
              </Heading>
              <div className={styles.sectionDescription}>Monitor and moderate content across topics with real-time case tracking and moderator activity.</div>
            </div>
            {isAbrFilteringEnabled && communityId && (
              <AbrFilterSelector communityId={communityId} selectedFilterId={selectedFilterId} onFilterChange={setSelectedFilterId} />
            )}
          </div>

          {/* Topics Table */}
          <ModerationQueueTable isLoading={isLoading} topics={topics} onStartModerate={handleStartModerate} />
        </div>
      </div>
    </div>
  );
}
